import { container } from 'inversify-props'
import { VendaServiceAdapter } from './adapter'
import { Desconto, CanaldeVendas, DetalhesDoPagamento, FiltroDeVendasSemNota, ItemDaVenda, ModalidadeDeVenda, Pagamento, TurnoDeVenda, Venda } from '@/models'
import {
	criarDevolucao,
	obterTrocoDaVenda,
	obterDescontoEmPercentual,
	unificarItensDaVendaPorProduto,
	imprimirNotaFiscal,
	obterDescontoEmPercentualDaTroca,
	emitirEComunicarDanfe,
	criarDevolucaoParaDemonstracaoOuConsignado,
	gerarNotaNaoEletronica,
} from '@/shareds/venda-shareds'
import { VendaModule } from '@/store/vuex/venda/VendaStore'
import AlertModule, { ErroComPrefixo } from '@/store/vuex/aplicacao/AlertModule'
import PluginModule from '@/store/vuex/aplicacao/PluginStore'
import { imprimirCupomSemValorFiscal } from '@/shareds/pedidos/impressao-cupom-sem-valor-fiscal'
import { FindEstoqueUseCase } from '../deposito'
import { PreVendaUseCase } from './PreVendaUseCase'
import { PreVenda } from '@/models/pre-venda/PreVenda'
import UserLoginStore from '@/store/vuex/authentication/UserLoginStore'
import { FindModalidadeDeVendaUseCase } from './FindModalidadeDeVendaUseCase'
import { FindVendaUseCase } from './FindVendaUseCase'
import { getCodigoProcesso } from '@/store/vuex/venda/EstadoProcessoVenda'
import { FindProdutoUseCase } from '../produto'
import { FindPromocaoUseCase } from './FindPromocaoUseCase'
import { PromocaoModule } from '@/store/vuex/venda/PromocaoStore'
import { FindCanalDeVendasUseCase } from './FindCanalDeVendasUseCase'

const findModalidadeDeVendaUseCase = new FindModalidadeDeVendaUseCase()
const findVendaUseCase = new FindVendaUseCase()
const findCanalDeVenda = new FindCanalDeVendasUseCase()
const findPromocaoUseCase = new FindPromocaoUseCase()

type UseCaseParams = {
	venda: Venda
	detalhesDosPagamentos: DetalhesDoPagamento[]
	turnoDeVenda: TurnoDeVenda
	service?: VendaServiceAdapter
	gerarCupom: boolean
	confirmacaoDeValeTroca?: { mostrar: () => Promise<boolean> }
	imprimirCupomSemValorFiscal?: (venda: Venda, pagamentos: DetalhesDoPagamento[]) => Promise<any>
	imprimirNotaFiscal?: (idVenda: string, imprimirNfe?: boolean, isContingencia?: boolean) => Promise<any>
	emitirEComunicarDanfe?: (idVenda: string) => Promise<any>
	gerarNotaNaoEletronica?: (idVenda: string) => Promise<any>
	vendaModule?: typeof VendaModule
	findEstoqueUseCase?: FindEstoqueUseCase
	itensDevolucao?: ItemDaVenda[]
	emitirDanfe: boolean
	preVendaUseCase?: PreVendaUseCase
	findProdutoUseCase?: FindProdutoUseCase
	
}

export async function FinalizarVendaUseCase({
	service = container.get<VendaServiceAdapter>('VendaServiceAdapter'),
	vendaModule = VendaModule,
	venda,
	detalhesDosPagamentos,
	gerarCupom,
	turnoDeVenda,
	findEstoqueUseCase = new FindEstoqueUseCase(),
	itensDevolucao,
	emitirDanfe,
	preVendaUseCase = new PreVendaUseCase(),
	findProdutoUseCase = new FindProdutoUseCase,
}: UseCaseParams): Promise<Venda> {
	if (!venda) throw new Error('Venda não informada')
	if (!turnoDeVenda) throw new Error('Turno de venda não informado')
	if (!venda.itens || venda.itens.length === 0) throw new Error('Venda não possui itens')

	const itensDeVitrine = venda.itens.filter(item => item.selecionadoViaVitrineVirtual)
	const itensNormais = venda.itens.filter(item => !item.selecionadoViaVitrineVirtual)

	const temItensDeVitrine = itensDeVitrine.length > 0
	const temItensNormais = itensNormais.length > 0

	venda.modalidadeDeVenda = venda.modalidadeDeVenda && typeof Object ? venda.modalidadeDeVenda : null
	let identificador = ''
	let vendaAEnviar: Venda | null
	const ehUmaTroca = venda.itens.filter(item => item.quantidade > 0).length > 0 && (venda.tipoDeTransacao === 'Devolução' || venda.tipoDeTransacao === 'Outros')
	const ehUmaVendaFical = !venda.possuiNotaNaoEletronica
	const ehUmaDemonstracaoReaberta = (venda.isDemonstracao || venda.isConsignado) && venda.isVendaReaberta
	const ehUmaVendaDeValePresente = venda?.itens.some(item => item.produto.isValePresente) ?? false

	if (venda.cliente && turnoDeVenda.id && turnoDeVenda.pontoDeVenda.loja.configuracaoDaLoja?.bloquearNovaVenda) {
		const podeVender = await service.podeVender(venda.cliente.cnpjOuCpf, turnoDeVenda.id)

		if (podeVender.length > 0) {
			throw Error(`Existe uma venda anterior com nota inválida (${podeVender[0]}), faça a correção dela para seguir com a venda atual.`)
		}
	}

	if (venda.tipoDeTransacao === 'Venda' && temItensDeVitrine){
		let modalidade
		let canalDeVenda

		try {
			modalidade = await findModalidadeDeVendaUseCase.findModalidadeDeVendaDaVitrineVirtual(turnoDeVenda.pontoDeVenda.loja.id)
		} catch(error: any){
			throw Error('Cadastrar modalidade de venda para o tipo Vitrine Virtual')
		}

		try {
			canalDeVenda = await findCanalDeVenda.findCanalDeVendaDaVitrineVirtual()
		} catch(error: any){
			throw Error('Cadastrar canal de venda para Vitrine Virtual')
		}
		
		if (!temItensNormais){
			return await finalizarSomenteItensDeVitrine({
				venda,
				itensDeVitrine,
				turnoDeVenda,
				service,
				detalhesDosPagamentos,
				modalidade,
				canalDeVenda,
			})
		} else {
			const totalNormal = itensNormais.reduce((acc, item) => acc + calcularValorLiquidoDoItem(item), 0)
			const totalVitrine = itensDeVitrine.reduce((acc, item) => acc + calcularValorLiquidoDoItem(item), 0)
			const totalGeral = totalNormal + totalVitrine
			const proporcaoVitrine = totalVitrine / totalGeral 
			const pagamentosVitrine = ratearPagamentos(venda.pagamentos, proporcaoVitrine)

			const { descontoVitrine, descontoNormal } = ratearDesconto(
				venda.desconto,
				totalVitrine,
				totalNormal,
			)

			const pagamentosNormais = venda.pagamentos.map((pagamento, index) => ({
				...pagamento,
				valor: Number((Number(pagamento.valor || 0) - Number(pagamentosVitrine[index]?.valor || 0)).toFixed(2)),
			})).filter(p => p.valor > 0)

			await finalizarItensDeVitrineSeparadamente({
				venda: {
					...venda,
					pagamentos: pagamentosVitrine,
					desconto: descontoVitrine,
				},
				itensDeVitrine,
				turnoDeVenda,
				service,
				modalidade,
				canalDeVenda,
			})

			venda = {
				...venda,
				itens: itensNormais,
				pagamentos: pagamentosNormais,
				desconto: descontoNormal,
			}
		}
	}

	await validarEstoque(venda, turnoDeVenda, ehUmaVendaFical, ehUmaTroca, findEstoqueUseCase)

	try {
		identificador = await service.gerarIdentificadorDeVenda(turnoDeVenda.pontoDeVenda.id)
	} catch (error: any) {
		throw Error('Falha ao finalizar a venda, tente novamente ou contate o suporte.')
	}


	const gerarEstoqueNaVenda = turnoDeVenda.pontoDeVenda.loja.configuracaoDaLoja?.podeGerarEstoqueNaVenda || false
	if ((!gerarEstoqueNaVenda || turnoDeVenda.pontoDeVenda.criaPedidoNaVenda) && venda.tipoDeTransacao === 'Venda' && !ehUmaDemonstracaoReaberta) {
		const itensUnificados = unificarItensDaVendaPorProduto(venda.itens)
		const podeLiberarEstoque = UserLoginStore.permiteRegraDeNegocio('pode-liberar-produto-sem-estoque') || UserLoginStore.autenticandoParaLiberarVendaSemEstoque
		let projecoesPorItemDaVenda
		if (!podeLiberarEstoque && !VendaModule.vendaAtual?.itens.some(item => item.produto.isValePresente)) {

			projecoesPorItemDaVenda = await Promise.all(
				itensUnificados.filter(item => item.quantidade != 0).map(async (itemDaVenda: any) => {
					let criticas = ''

					if (!itemDaVenda.autenticadoParaLiberarProdutoSemEstoque) {
						const quantidadeADevolver = (
							itensDevolucao?.filter(i => i.id = itemDaVenda.id)
								.reduce((total, { quantidade }) => total + quantidade, 0) || 0) - itemDaVenda.quantidade

						await findEstoqueUseCase.getEstoqueDoItemDaVendaNaLoja(
							itemDaVenda.id, quantidadeADevolver.toString(), turnoDeVenda?.pontoDeVenda.loja.id || '', itemDaVenda.autenticadoParaLiberarProdutoSemEstoque,
						).catch((error) => {
							criticas = error instanceof Object ? error.response.data[0] : error
						})
					}

					return criticas
				}),
			)
			projecoesPorItemDaVenda = projecoesPorItemDaVenda.filter(value => value !== '')
		}
		const criticasNormalizadas = projecoesPorItemDaVenda
			? projecoesPorItemDaVenda.join('<br>')
			: undefined
		if (criticasNormalizadas) {
			throw criticasNormalizadas
		}
	}

	if (ehUmaDemonstracaoReaberta) {
		return processarVendaDemonstracaoOuConsignado(venda, turnoDeVenda, gerarCupom, emitirDanfe, detalhesDosPagamentos,
			service, identificador, itensDevolucao, ehUmaVendaFical)
	} else {
		const codigoDoProcesso = VendaModule.etapaVenda ? getCodigoProcesso(VendaModule.etapaVenda.estado) || 0 : 0

		if (venda.tipoDeTransacao === 'Venda') {
			const pagamentosDeVale = venda.pagamentos.filter(p => !!p.valePresenteId)

			for (const index in pagamentosDeVale) {
				const pagamentoVale = pagamentosDeVale[index]
				const identificadorDevolucao = await service.gerarIdentificadorDeVenda(turnoDeVenda.pontoDeVenda.id)

				const valePresenteIndex = venda.valesPresentes.findIndex(vale => vale.id === pagamentoVale.valePresenteId)

				if (valePresenteIndex >= 0) {
					const valeEncontrado = venda.valesPresentes[valePresenteIndex]
					const vendaOrigemDoVale = await findVendaUseCase.get(valeEncontrado.vendaId)

					const itensFiltrados = vendaOrigemDoVale.itens.filter(item => item.preco ===  valeEncontrado.valor)

					if (itensFiltrados.length > 0) {

						const vendedorVendaAtual = !!venda.vendedor && venda.vendedor.length > 0 ? venda.vendedor[0] : null

						const itemDevolucao = {
							id: '',
							produto: itensFiltrados[0].produto,
							quantidade: 1,
							quantidadeLida: 0,
							desconto: { isPercentual: true, valor: 0.0 },
							preco: pagamentoVale.valor,
							precoDeCusto: 0,
							idConjunto: null,
							descontoVarejo: 0,
							percentualCashback: 0,
							diasParaEfetivarCashback: 0,
							isBrinde: false,
							idItemNotaOrigem: null,
							chaveNotaOrigem: null,
							diasParaExpirarCashback: 0,
							possuiDescontoPorTabela: false,
							valorDescontoRateadoDoItem: 0,
							itemDeDevolucao: false,
							isTotalmenteTrocado: false,
							motivoCancelamento: null,
							isCompra: false,
							idItemOrigem: '',
							isDaVendaReaberta: false,
							vendedor: vendedorVendaAtual,
							autenticadoParaLiberarProdutoSemEstoque: false,
							vendaOrigemDoItem: null,
							valorFreteRateado: 0,
							isItemReferenteATroca: false,
							isDeOutraLoja: false,
							percentualDescontoTabelaPreco: 0,
							precoDeCustoReal: 0,
						}

						const tipoDePagamentoVoucher = turnoDeVenda?.tiposDePagamento.filter(tipo => tipo.formaDePagamento === 'Voucher')

						if (tipoDePagamentoVoucher.length === 0) {
							throw Error('Tipo de pagamento Voucher não configurado para a loja')
						}

						const devolucaoValePresente: Venda = {
							...venda,
							id: '',
							troco: 0,
							tipoDeCliente: venda.tipoDeCliente || null,
							tipoDeTransacao: 'Devolução',
							pontoDeVenda: turnoDeVenda.pontoDeVenda,
							dataHora: new Date(),
							itens: [itemDevolucao],
							identificador: identificadorDevolucao,
							pagamentos: [{
								id: '',
								valor: pagamentoVale.valor,
								tipoDePagamento: tipoDePagamentoVoucher[0],
								dataHoraPagamento: new Date(),
								qtdeDeParcelas: 0,
								status: 'Pago',
								valorDaTaxa: null,
								detalhesDoPagamentoId: null,
								link: null,
								valePresenteId: valeEncontrado.id, //Registra o ID do vale presente que está sendo utilizado na venda atual
								linkIdAsaas: null,
								detalhesPagamentoEmChequeId: null,
								devolucaoValePresenteId: null,
							}],
							tipoNota: 'Entrada',
							vendaOrigem: valeEncontrado.vendaId,
							isOrcamento: true,
							desconto: {
								isPercentual: false,
								valor: 0,
							},
						}
			
						const devolucaoValePresenteCriada = await service.create(devolucaoValePresente, false, false)

						venda.pagamentos.forEach(p => {
							if (p.valePresenteId === valeEncontrado.id) {
								p.devolucaoValePresenteId = devolucaoValePresenteCriada.id
							}
						})
					}
				}
			}
		}

		if (ehUmaTroca && venda.itens.filter(item => item.quantidade > 0).length > 0) {
			vendaAEnviar = {
				...venda,
				troco: obterTrocoDaVenda(venda),
				tipoDeCliente: venda.tipoDeCliente || null,
				pontoDeVenda: turnoDeVenda.pontoDeVenda,
				dataHora: venda.dataHora || new Date(),
				identificador: venda.identificador,
				itens: venda.itens.filter(item => item.quantidade > 0),
				isConsignado: venda.isVendaReaberta ? false : venda.isConsignado,
				isDemonstracao: venda.isVendaReaberta ? false : venda.isDemonstracao,
				cpfDoCliente: venda.cliente ? venda.cliente.cnpjOuCpf : '',
				isOrcamento: !gerarCupom,
				vendaOrigem: venda.vendaOrigem,
				pagamentos: venda.pagamentos.filter(pagamento => pagamento.valor > 0) || [],
				tipoDeTransacao: 'Venda',
				tipoNota: 'Saída',
				isTroca: true,
				desconto: obterDescontoEmPercentualDaTroca(venda),
				pedido: turnoDeVenda.pontoDeVenda.criaPedidoNaVenda ? venda.pedido : null,
				autenticandoParaLiberarVendaSemEstoque: venda.autenticandoParaLiberarVendaSemEstoque,
			}

		} else {

			vendaAEnviar = {
				...venda,
				troco: obterTrocoDaVenda(venda),
				tipoDeCliente: venda.tipoDeCliente || null,
				pontoDeVenda: turnoDeVenda.pontoDeVenda,
				dataHora: venda.dataHora || new Date(),
				identificador: venda.identificador,
				itens: venda.itens.filter(item => item.quantidade > 0 || item.quantidade < 0),
				isConsignado: venda.isVendaReaberta ? false : venda.isConsignado,
				isDemonstracao: venda.isVendaReaberta ? false : venda.isDemonstracao,
				cpfDoCliente: venda.cliente ? venda.cliente.cnpjOuCpf : '',
				isOrcamento: !gerarCupom,
				vendaOrigem: venda.vendaOrigem,
				isTroca: false,
				desconto: obterDescontoEmPercentual(venda),
				pedido: null,
				demonstracaoConvertida: venda.isDemonstracao && venda.isVendaReaberta,
				consignacaoConvertida: venda.isConsignado && venda.isVendaReaberta,
				autenticandoParaLiberarVendaSemEstoque: UserLoginStore.autenticandoParaLiberarVendaSemEstoque,
			}
		}

		const apenasDevolucaoDeDemonstracao = vendaAEnviar.itens.filter(item => item.quantidade !== 0).length === 0
			&& venda.isVendaReaberta
			&& (venda.isConsignado || venda.isDemonstracao)

		if (apenasDevolucaoDeDemonstracao && itensDevolucao) {

			let identificadorDevolucao = ''
			try {
				identificadorDevolucao = await service.gerarIdentificadorDeVenda(turnoDeVenda.pontoDeVenda.id)
			} catch (error: any) {
				throw Error('Falha ao finalizar a venda, tente novamente ou contate o suporte.')
			}

			const vendaOriginal: Venda = {
				...vendaAEnviar,
				id: '',
				troco: obterTrocoDaVenda(venda),
				tipoDeCliente: venda.tipoDeCliente || null,
				tipoDeTransacao: 'Devolução',
				pontoDeVenda: turnoDeVenda.pontoDeVenda,
				dataHora: new Date(),
				identificador: venda.identificador,
				vendaOrigem: venda.id,
				itens: itensDevolucao,
			}

			let vendaDevolucao: Venda = {
				...criarDevolucaoParaDemonstracaoOuConsignado({
					itens: itensDevolucao,
					vendaOrigem: venda,
					vendaOriginal: vendaOriginal,
					...turnoDeVenda.pontoDeVenda.parametrosFiscais,
					tipoDeTransacao: 'Devolução',
				}),
				pontoDeVenda: turnoDeVenda.pontoDeVenda,
				dataHora: new Date(),
				identificador: identificadorDevolucao,
				isConsignado: venda.isConsignado,
				isDemonstracao: venda.isDemonstracao,
				cliente: venda.cliente,
				vendaOrigem: venda.vendaOrigem,
			}

			if (codigoDoProcesso < 25) {
				if (vendaDevolucao.isConsignado) {
					VendaModule.setEtapaVenda({
						...VendaModule.etapaVenda,
						estado: 'PROCESSANDO_DEVOLUCAO_VENDA_CON_DEM',
					})

					await service.devolucaoVendaConsignada(vendaDevolucao)

					VendaModule.setEtapaVenda({
						vendas: [vendaDevolucao],
						estado: 'PROCESSO_DEVOLUCAO_VENDA_CON_DEM_CONCLUIDO',
					})
				} else {
					VendaModule.setEtapaVenda({
						...VendaModule.etapaVenda,
						estado: 'PROCESSANDO_DEVOLUCAO_VENDA_CON_DEM',
					})
					await service.devolucaoVendaDemonstracao(vendaDevolucao)
					VendaModule.setEtapaVenda({
						vendas: [vendaDevolucao],
						estado: 'PROCESSO_DEVOLUCAO_VENDA_CON_DEM_CONCLUIDO',
					})
				}
			}

			if (codigoDoProcesso < 27) {
				try {
					vendaDevolucao = VendaModule.etapaVenda.vendas[0]
					VendaModule.setEtapaVenda({
						...VendaModule.etapaVenda,
						estado: 'PROCESSANDO_EMISSAO_DANFE_DEVOLUCAO',
					})
					await emitirDanfePorVendaId(vendaDevolucao.id)
					VendaModule.setEtapaVenda({
						vendas: [],
						estado: 'PROCESSO_EMISSAO_DANFE_DEVOLUCAO_CONCLUIDO',
					})
				} catch (error: any) {
					const erroComPrefixo = {
						error: error,
						prefixo: 'Erro ao emitir danfe do retorno, verifique na tela de vendas: ',
					} as ErroComPrefixo
					AlertModule.setErrorComPrefixo(erroComPrefixo)
				}
			}

			vendaModule.iniciarNovaVenda({ identificador, novaVendaReaberta: false })

			return vendaDevolucao
		}

		if ((venda.isConsignado || venda.isDemonstracao) && venda.isVendaReaberta && itensDevolucao && itensDevolucao.length > 0) {
			let identificadorDevolucao = ''
			try {
				identificadorDevolucao = await service.gerarIdentificadorDeVenda(turnoDeVenda.pontoDeVenda.id)
			} catch (error: any) {
				throw Error('Falha ao finalizar a venda, tente novamente ou contate o suporte.')
			}

			const vendaOriginal: Venda = {
				...vendaAEnviar,
				id: '',
				troco: obterTrocoDaVenda(venda),
				tipoDeCliente: venda.tipoDeCliente || null,
				tipoDeTransacao: 'Devolução',
				pontoDeVenda: turnoDeVenda.pontoDeVenda,
				dataHora: new Date(),
				identificador: venda.identificador,
				vendaOrigem: vendaAEnviar.id,
				itens: itensDevolucao,
			}

			let vendaDevolucao: Venda = {
				...criarDevolucaoParaDemonstracaoOuConsignado({
					itens: itensDevolucao,
					vendaOrigem: vendaAEnviar,
					vendaOriginal: vendaOriginal,
					...turnoDeVenda.pontoDeVenda.parametrosFiscais,
					tipoDeTransacao: 'Devolução',
				}),
				pontoDeVenda: turnoDeVenda.pontoDeVenda,
				dataHora: new Date(),
				identificador: identificadorDevolucao,
				isConsignado: venda.isConsignado,
				isDemonstracao: venda.isDemonstracao,
				cliente: venda.cliente,
				vendaOrigem: vendaAEnviar.vendaOrigem,
				desconto: null,
			}

			if (codigoDoProcesso < 25) {
				if (vendaDevolucao.isConsignado) {
					VendaModule.setEtapaVenda({
						...VendaModule.etapaVenda,
						estado: 'PROCESSANDO_DEVOLUCAO_VENDA_CON_DEM',
					})
					await service.devolucaoVendaConsignada(vendaDevolucao)
					VendaModule.setEtapaVenda({
						vendas: [vendaDevolucao],
						estado: 'PROCESSO_DEVOLUCAO_VENDA_CON_DEM_CONCLUIDO',
					})
				} else {
					VendaModule.setEtapaVenda({
						...VendaModule.etapaVenda,
						estado: 'PROCESSANDO_DEVOLUCAO_VENDA_CON_DEM',
					})
					await service.devolucaoVendaDemonstracao(vendaDevolucao)
					VendaModule.setEtapaVenda({
						vendas: [vendaDevolucao],
						estado: 'PROCESSO_DEVOLUCAO_VENDA_CON_DEM_CONCLUIDO',
					})
				}
			}

			if (codigoDoProcesso < 27) {
				try {
					VendaModule.setEtapaVenda({
						...VendaModule.etapaVenda,
						estado: 'PROCESSANDO_EMISSAO_DANFE_DEVOLUCAO',
					})
					vendaDevolucao = VendaModule.etapaVenda.vendas[0]
					await emitirDanfePorVendaId(vendaDevolucao.id)
					VendaModule.setEtapaVenda({
						vendas: [],
						estado: 'PROCESSO_EMISSAO_DANFE_DEVOLUCAO_CONCLUIDO',
					})
				} catch (error: any) {
					const erroComPrefixo = {
						error: error,
						prefixo: 'Erro ao emitir danfe do retorno, verifique na tela de vendas: ',
					} as ErroComPrefixo
					AlertModule.setErrorComPrefixo(erroComPrefixo)
				}
			}
		}

		const itensDevolucaoSemOrigem = venda.itens.filter(
			item =>
				(!item.idItemOrigem || item.idItemOrigem === undefined) &&
				item.quantidade < 0,
		)

		const itensDevolucaoComOrigem = venda.itens.filter(
			item =>
				(item.idItemOrigem) &&
				item.quantidade < 0,
		)

		const trocaComESemOrigem = itensDevolucaoSemOrigem.length > 0 && itensDevolucaoComOrigem.length > 0

		if (ehUmaTroca && !trocaComESemOrigem) {
			let identificadorDevolucao = ''
			try {
				identificadorDevolucao = await service.gerarIdentificadorDeVenda(turnoDeVenda.pontoDeVenda.id)
			} catch (error: any) {
				throw Error('Falha ao finalizar a venda, tente novamente ou contate o suporte.')
			}

			const vendaOriginal: Venda = {
				...venda,
				id: '',
				troco: 0,
				tipoDeCliente: venda.tipoDeCliente || null,
				tipoDeTransacao: venda.tipoDeTransacao,
				pontoDeVenda: turnoDeVenda.pontoDeVenda,
				dataHora: new Date(),
				vendaOrigem: venda.vendaOrigem,
			}

			const vendaDevolucao: Venda = {
				...criarDevolucao({
					itens: venda.itens.filter(item => item.quantidade < 0),
					vendaOrigem: venda,
					vendaOriginal: vendaOriginal,
					...turnoDeVenda.pontoDeVenda.parametrosFiscais,
					tipoDeTransacao: 'Devolução',
				}),
				pontoDeVenda: turnoDeVenda.pontoDeVenda,
				dataHora: new Date(),
				identificador: identificadorDevolucao,
				cliente: venda.cliente,
				pagamentos: venda.pagamentos.filter(pagamento => pagamento.valor < 0) || [],
				isTroca: true,
				vendaOrigem: vendaOriginal.vendaOrigem,
				tipoDeTransacao: venda.tipoDeTransacao,
				loja: venda.pontoDeVenda != null ? venda.pontoDeVenda.loja.id : venda.loja,
			}

			const gerarNotaEletronica = gerarCupom || emitirDanfe

			if (codigoDoProcesso < 29) {
				VendaModule.setEtapaVenda({
					...VendaModule.etapaVenda,
					estado: 'PROCESSANDO_DEVOLUCAO_DA_TROCA',
				})
				await service.create(vendaDevolucao, false, gerarNotaEletronica)
				VendaModule.setEtapaVenda({
					vendas: [],
					estado: 'PROCESSO_DEVOLUCAO_DA_TROCA_CONCLUIDO',
				})
			}
		}
		else if (trocaComESemOrigem) {
			const itensVenda = venda.itens.filter(item => item.quantidade > 0)

			const vendaAtualizada = await processarTrocaComOuSemOrigem(
				vendaAEnviar,
				venda,
				turnoDeVenda,
				gerarCupom,
				emitirDanfe,
				service,
				itensDevolucaoSemOrigem,
				itensDevolucaoComOrigem,
				itensVenda,
				detalhesDosPagamentos,
			)

			localStorage.setItem('ULTIMA_VENDA', JSON.stringify(vendaAtualizada))

			VendaModule.iniciarNovaVenda({
				identificador: vendaAtualizada.identificador || '',
				novaVendaReaberta: false,
			});

			UserLoginStore.setAutenticandoParaLiberarVendaSemEstoque(false)

			return vendaAtualizada
		}

		const vendaACriar: Venda = {
			...vendaAEnviar,
			cpfDoCliente: venda.cpfDoCliente,
			existeValePresente: ehUmaVendaDeValePresente,
			loja: venda.pontoDeVenda != null ? venda.pontoDeVenda.loja.id : venda.loja,
		}

		if (codigoDoProcesso <= 34) {
			VendaModule.setEtapaVenda({
				...VendaModule.etapaVenda,
				estado: 'PROCESSANDO_VENDA_FINAL',
			})
			const vendaAtualizada = await service.create(vendaACriar, emitirDanfe, gerarCupom || emitirDanfe || !!vendaACriar.pontoDeVenda?.criaPedidoNaVenda)
			const sorteioSelecionado = PromocaoModule.dados.sorteioSelecionado
			if (sorteioSelecionado) {
				try {
					const numerosSorteados = await findPromocaoUseCase.gerarNumerosDeSorteioDaVenda(
						vendaAtualizada.id,
						sorteioSelecionado,
					)

					vendaAtualizada.numerosDoSorteio = numerosSorteados
				} catch (error: any) {
					AlertModule.setWarning(
						'Venda finalizada com sucesso, mas ocorreu um erro ao gerar os números do sorteio. Verifique a venda e tente gerar os números depois.',
					)
				}
			}
			VendaModule.setEtapaVenda({
				vendas: [vendaAtualizada],
				estado: 'PROCESSO_VENDA_FINAL_CONLCUIDO',
			})
			
			// Nova venda
			if (!venda.identificador) vendaModule.adicionarVendaDoTurno(vendaAtualizada)
			await imprimirCupomOuDanfe(vendaAtualizada, gerarCupom, emitirDanfe, detalhesDosPagamentos, false)

			if (VendaModule.vendaAtual?.idPreVenda) {
				try {
					const preVenda: PreVenda = {
						id: VendaModule.vendaAtual?.idPreVenda,
						identificador: '',
						situacao: 'Concluído',
						dataHoraCriacao: null,
						itens: [],
						loja: null,
						cpfOuCnpjDoCliente: VendaModule.vendaAtual.cpfDoCliente,
						vendedor: null,
						tipoDePreVenda: 'venda',
						vendaIdOrigem: '',
						representante: null,
						percentualRepresentante: 0,
						volumes: 0,
						movimentouEstoque: false,
						geradaDeUmaVenda: false,
					}
					await preVendaUseCase.updateSituacao(preVenda)
					VendaModule.recarregaVenda
				} catch (error) {
					throw Error('Falha ao finalizar a venda, tente novamente ou contate o suporte.')
				}
			}

			localStorage.setItem('ULTIMA_VENDA', JSON.stringify(vendaAtualizada))
			await findProdutoUseCase.finalizarProcessoRfidCaixa(turnoDeVenda.pontoDeVenda.id)
			localStorage.setItem('VENDA_CONFERIDA_POR_RFID', JSON.stringify(false))

			vendaModule.iniciarNovaVenda({ identificador, novaVendaReaberta: false })
			UserLoginStore.setAutenticandoParaLiberarVendaSemEstoque(false)

			return vendaAtualizada
		} else {
			AlertModule.setSuccess('Venda finalizada')
			const vendaAtualizada = VendaModule.etapaVenda.vendas[0]
			vendaModule.iniciarNovaVenda({ identificador, novaVendaReaberta: false })
			UserLoginStore.setAutenticandoParaLiberarVendaSemEstoque(false)
			return codigoDoProcesso == 35 ? vendaAtualizada : vendaACriar
		}
	}
}

async function processarTrocaComOuSemOrigem(vendaAEnviar: Venda, venda: Venda, turnoDeVenda: TurnoDeVenda, gerarCupom: boolean,
	emitirDanfe: boolean, service: VendaServiceAdapter, itensDevolucaoSemOrigem: ItemDaVenda[],
	itensDevolucaoComOrigem: ItemDaVenda[], itensVenda: ItemDaVenda[], detalhesDosPagamentos: DetalhesDoPagamento[]): Promise<Venda> {
	const codigoDoProcesso = VendaModule.etapaVenda ? getCodigoProcesso(VendaModule.etapaVenda.estado) || 0 : 0

	try {
		let devolucaoAtualizada: Venda = {} as Venda
		let montantePagamento = Math.abs(venda.pagamentos.reduce((total, pagamento) => total + pagamento.valor, 0))

		if (itensDevolucaoComOrigem.length > 0) {
			let identificadorDevolucao = ''
			try {
				identificadorDevolucao = await service.gerarIdentificadorDeVenda(turnoDeVenda.pontoDeVenda.id)
			} catch (error: any) {
				throw Error('Falha ao finalizar a venda, tente novamente ou contate o suporte.')
			}

			const vendaOriginal: Venda = {
				...venda,
				id: '',
				troco: 0,
				tipoDeCliente: venda.tipoDeCliente || null,
				tipoDeTransacao: venda.tipoDeTransacao,
				pontoDeVenda: turnoDeVenda.pontoDeVenda,
				dataHora: new Date(),
				vendaOrigem: venda.vendaOrigem,
			}

			const vendaDevolucao: Venda = {
				...criarDevolucao({
					itens: itensDevolucaoComOrigem,
					vendaOrigem: venda,
					vendaOriginal: vendaOriginal,
					...turnoDeVenda.pontoDeVenda.parametrosFiscais,
					tipoDeTransacao: 'Devolução',
				}),
				pontoDeVenda: turnoDeVenda.pontoDeVenda,
				dataHora: new Date(),
				identificador: identificadorDevolucao,
				cliente: venda.cliente,
				pagamentos: [],
				isTroca: true,
				vendaOrigem: vendaOriginal.vendaOrigem,
				tipoDeTransacao: 'Devolução',
				loja: venda.pontoDeVenda != null ? venda.pontoDeVenda.loja.id : venda.loja,
			}

			if (venda.pagamentos.length > 0) {
				const valorPagamento = Math.min(
					montantePagamento,
					itensDevolucaoComOrigem.reduce((total, item) => total + item.preco, 0),
				)

				const pagamentos = [{
					...venda.pagamentos[0],
					valor: -valorPagamento,
				}]

				vendaDevolucao.pagamentos = pagamentos
				montantePagamento = Math.max(0, Number((Number(montantePagamento) - Number(valorPagamento)).toFixed(2)))
			}

			VendaModule.setEtapaVenda({
				...VendaModule.etapaVenda,
				estado: 'PROCESSANDO_DEVOLUCAO_DA_TROCA_COM_IDENTIFICACAO',
			})
			devolucaoAtualizada = await service.create(vendaDevolucao, false, gerarCupom || emitirDanfe || !!vendaDevolucao.pontoDeVenda?.criaPedidoNaVenda)

			VendaModule.setEtapaVenda({
				vendas: [],
				estado: 'PROCESSO_DEVOLUCAO_DA_TROCA_COM_IDENTIFICACAO',
			})
		}

		if (itensDevolucaoSemOrigem.length > 0) {
			let identificadorDevolucao = ''
			try {
				identificadorDevolucao = await service.gerarIdentificadorDeVenda(turnoDeVenda.pontoDeVenda.id)
			} catch (error: any) {
				throw Error('Falha ao finalizar a venda, tente novamente ou contate o suporte.')
			}

			const vendaOriginal: Venda = {
				...venda,
				id: '',
				troco: 0,
				tipoDeCliente: venda.tipoDeCliente || null,
				tipoDeTransacao: venda.tipoDeTransacao,
				pontoDeVenda: turnoDeVenda.pontoDeVenda,
				dataHora: new Date(),
				vendaOrigem: venda.vendaOrigem,
			}

			const vendaDevolucao: Venda = {
				...criarDevolucao({
					itens: itensDevolucaoSemOrigem,
					vendaOrigem: venda,
					vendaOriginal: vendaOriginal,
					...turnoDeVenda.pontoDeVenda.parametrosFiscais,
					tipoDeTransacao: venda.tipoDeTransacao,
				}),
				pontoDeVenda: turnoDeVenda.pontoDeVenda,
				dataHora: new Date(),
				identificador: identificadorDevolucao,
				cliente: venda.cliente,
				pagamentos: venda.pagamentos.filter(pagamento => pagamento.valor < 0) || [],
				isTroca: true,
				vendaOrigem: vendaOriginal.vendaOrigem,
				tipoDeTransacao: 'Outros',
				loja: venda.pontoDeVenda != null ? venda.pontoDeVenda.loja.id : venda.loja,
			}

			if (venda.pagamentos.length > 0) {
				const pagamentos = [{
					...venda.pagamentos[0],
					valor: montantePagamento > 0 ? -montantePagamento : montantePagamento,
				}]

				vendaDevolucao.pagamentos = pagamentos
			}

			if (codigoDoProcesso < 30) {
				VendaModule.setEtapaVenda({
					...VendaModule.etapaVenda,
					estado: 'PROCESSANDO_DEVOLUCAO_DA_TROCA_SEM_IDENTIFICACAO',
				})
				const vendaAtualizada = await service.create(vendaDevolucao, false, gerarCupom || emitirDanfe || !!vendaDevolucao.pontoDeVenda?.criaPedidoNaVenda)
				VendaModule.setEtapaVenda({
					vendas: [],
					estado: 'PROCESSO_DEVOLUCAO_DA_TROCA_SEM_IDENTIFICACAO_CONCLUIDO',
				})

				if (!venda.identificador) VendaModule.adicionarVendaDoTurno(vendaAtualizada)
				if (itensVenda.length === 0) {

					return vendaAtualizada
				}
			}
		}

		if (venda.itens.length > 0) {
			let identificador = ''
			try {
				identificador = await service.gerarIdentificadorDeVenda(turnoDeVenda.pontoDeVenda.id)
			} catch (error: any) {
				throw Error('Falha ao finalizar a venda, tente novamente ou contate o suporte.')
			}

			const vendaACriar: Venda = {
				...vendaAEnviar,
				itens: itensVenda,
				troco: 0,
				tipoDeCliente: venda.tipoDeCliente || null,
				pontoDeVenda: turnoDeVenda.pontoDeVenda,
				dataHora: new Date(),
				vendaOrigem: venda.vendaOrigem,
				identificador: identificador,
				cpfDoCliente: venda.cpfDoCliente,
				tipoDeTransacao: 'Venda',
				loja: venda.pontoDeVenda != null ? venda.pontoDeVenda.loja.id : venda.loja,
			}
			const vendaAtualizada = await service.create(vendaACriar, emitirDanfe, gerarCupom || emitirDanfe || !!vendaACriar.pontoDeVenda?.criaPedidoNaVenda)
			const sorteioSelecionado = PromocaoModule.dados.sorteioSelecionado
			if (sorteioSelecionado) {
				try {
					const numerosSorteados = await findPromocaoUseCase.gerarNumerosDeSorteioDaVenda(
						vendaAtualizada.id,
						sorteioSelecionado,
					)

					vendaAtualizada.numerosDoSorteio = numerosSorteados
				} catch (error: any) {
					AlertModule.setWarning(
						'Venda finalizada com sucesso, mas ocorreu um erro ao gerar os números do sorteio. Verifique a venda e tente gerar os números depois.',
					)
				}
			}

			if (!venda.identificador) VendaModule.adicionarVendaDoTurno(vendaAtualizada)
			await imprimirCupomOuDanfe(vendaAtualizada, gerarCupom, emitirDanfe, detalhesDosPagamentos, false)

			return vendaAtualizada
		}

		return devolucaoAtualizada
	} catch (e) {
		throw Error('Falha ao finalizar a troca, tente novamente ou contate o suporte.')
	} finally {
		AlertModule.setSuccess("Devolução realizada com sucesso")
	}
}

async function processarVendaDemonstracaoOuConsignado(venda: Venda, turnoDeVenda: TurnoDeVenda, gerarCupom: boolean,
	emitirDanfe: boolean, detalhesDosPagamentos: DetalhesDoPagamento[], service: VendaServiceAdapter,
	identificador: string, itensDevolucao: ItemDaVenda[] | undefined, ehUmaVendaFiscal: boolean): Promise<Venda> {

	const tipoPagamentoDemonstracao = turnoDeVenda?.tiposDePagamento
		.filter(tipo => tipo.formaDePagamento === 'Demonstração' || tipo.formaDePagamento === 'Consignado')

	if (tipoPagamentoDemonstracao.length === 0) {
		if (venda.isDemonstracao) {
			throw new Error('Para realizar essa operação cadastre uma forma de pagamento de Demonstração')
		} else {
			throw new Error('Para realizar essa operação cadastre uma forma de pagamento de Consignado')
		}
	}

	const itensDaDemonstracao = venda.itens.filter(item => item.isCompra === false && item.quantidade > 0)

	const vendasAEnviar: Venda[] = []
	if (itensDaDemonstracao.length > 0) {
		const totalPrecoDemonstracao = itensDaDemonstracao.length > 0
			? itensDaDemonstracao.reduce((total, item) => total + item.preco * item.quantidade, 0)
			: 0

		const pagamentoDemonstracao: Pagamento[] = [{
			id: '',
			valor: totalPrecoDemonstracao,
			tipoDePagamento: tipoPagamentoDemonstracao[0],
			dataHoraPagamento: new Date(),
			qtdeDeParcelas: 0,
			status: 'Pago',
			valorDaTaxa: null,
			detalhesDoPagamentoId: null,
			link: null,
			valePresenteId: null,
			linkIdAsaas: null,
			detalhesPagamentoEmChequeId: null,
			devolucaoValePresenteId: null,
		}]

		venda.autenticandoParaLiberarVendaSemEstoque = UserLoginStore.autenticandoParaLiberarVendaSemEstoque

		const vendaDemonstracao: Venda = {
			...venda,
			id: '',
			identificador: '',
			troco: obterTrocoDaVenda(venda),
			tipoDeCliente: venda.tipoDeCliente || null,
			pontoDeVenda: turnoDeVenda.pontoDeVenda,
			dataHora: venda.dataHora || new Date(),
			itens: itensDaDemonstracao,
			isConsignado: venda.isConsignado,
			isDemonstracao: venda.isDemonstracao,
			cpfDoCliente: venda.cliente ? venda.cliente.cnpjOuCpf : '',
			isOrcamento: !gerarCupom,
			vendaOrigem: venda.vendaOrigem,
			pagamentos: pagamentoDemonstracao,
			tipoDeTransacao: 'Venda',
			tipoNota: 'Saída',
			isTroca: false,
			desconto: null,
			pedido: null,
			demonstracaoConvertida: false,
			consignacaoConvertida: false,
			loja: venda.pontoDeVenda ? venda.pontoDeVenda.loja.id : "",
			cashbackAplicado: false,
			valorDeCashbackAplicado: 0,
		}

		vendasAEnviar.push(vendaDemonstracao)
	}

	const itensDaVenda = venda.itens.filter(item => item.isCompra === true && item.quantidade > 0)

	if (itensDaVenda.length > 0) {
		const params = {
			isDevolucao: false,
			isDemonstracao: true,
			isVenda: false,
		}
		let modalidade
		if (venda.pontoDeVenda) {
			modalidade = await findModalidadeDeVendaUseCase.findModalidadePadrao(venda.pontoDeVenda.loja.id, params)
		}
		const vendaNormal: Venda = {
			...venda,
			id: '',
			troco: obterTrocoDaVenda(venda),
			tipoDeCliente: venda.tipoDeCliente || null,
			pontoDeVenda: turnoDeVenda.pontoDeVenda,
			dataHora: venda.dataHora || new Date(),
			itens: itensDaVenda,
			isConsignado: false,
			isDemonstracao: false,
			cpfDoCliente: venda.cliente ? venda.cliente.cnpjOuCpf : '',
			isOrcamento: !gerarCupom,
			vendaOrigem: venda.vendaOrigem,
			pagamentos: venda.pagamentos.filter(pagamento => pagamento.valor > 0) || [],
			tipoDeTransacao: 'Venda',
			tipoNota: 'Saída',
			modalidadeDeVenda: modalidade,
			demonstracaoConvertida: venda.isDemonstracao && venda.isVendaReaberta,
			consignacaoConvertida: venda.isConsignado && venda.isVendaReaberta,
		}

		vendasAEnviar.push(vendaNormal)
	}

	const codigoDoProcesso = getCodigoProcesso(VendaModule.etapaVenda.estado) || 0

	//Processo de devolução de Demonstração/Consignado
	if (itensDevolucao && itensDevolucao.length > 0) {
		const itensDevolucaoEletronica = itensDevolucao.filter(item => !item.itemVendaNaoEletronica)
		const itensDevolucaoNaoEletronica = itensDevolucao.filter(item => item.itemVendaNaoEletronica)
		const vendasOrigemSet = new Set<string>();

		if (venda.vendaOrigem) {
			vendasOrigemSet.add(venda.vendaOrigem);
		} else {
			itensDevolucao.forEach(item => {
				if (item.vendaOrigemDoItem) {
					vendasOrigemSet.add(item.vendaOrigemDoItem);
				}
			});
		}
		const vendasOrigemUnicas = Array.from(vendasOrigemSet);
		const possuiDevolucaoDaVendaOrigem = vendasOrigemUnicas.length > 0 ? await findVendaUseCase.possuiDevolucaoDaVendaOrigem(vendasOrigemUnicas) : false

		if (itensDevolucaoNaoEletronica.length > 0 && codigoDoProcesso < 7) {
			const vendaOriginal: Venda = {
				...venda,
				id: '',
				troco: obterTrocoDaVenda(venda),
				tipoDeCliente: venda.tipoDeCliente || null,
				tipoDeTransacao: 'Devolução',
				pontoDeVenda: turnoDeVenda.pontoDeVenda,
				dataHora: new Date(),
				identificador: venda.identificador,
				itens: itensDevolucaoNaoEletronica,
			}

			VendaModule.setEtapaVenda({
				...VendaModule.etapaVenda,
				estado: 'INICIANDO_DEVOLUCAO_NNE',
			})

			if (vendasAEnviar.length === 0 && itensDevolucaoEletronica.length === 0) {
				const devolucaoNaoEletronica = await processarVendaDevolucaoNaoEletronica(venda, turnoDeVenda, service,
					itensDevolucaoNaoEletronica, vendaOriginal, codigoDoProcesso)
				VendaModule.iniciarNovaVenda({ identificador, novaVendaReaberta: false })
				UserLoginStore.setAutenticandoParaLiberarVendaSemEstoque(false)

				return devolucaoNaoEletronica
			} else if (!possuiDevolucaoDaVendaOrigem) {
				await processarVendaDevolucaoNaoEletronica(venda, turnoDeVenda, service,
					itensDevolucaoNaoEletronica, vendaOriginal, codigoDoProcesso)

				VendaModule.setEtapaVenda({
					...VendaModule.etapaVenda,
					estado: 'DEVOLUCAO_NNE_CONCLUIDA',
				})
			}
		}

		if (itensDevolucaoEletronica.length > 0 && codigoDoProcesso < 15) {
			const vendaOriginal: Venda = {
				...venda,
				id: '',
				troco: obterTrocoDaVenda(venda),
				tipoDeCliente: venda.tipoDeCliente || null,
				tipoDeTransacao: 'Devolução',
				pontoDeVenda: turnoDeVenda.pontoDeVenda,
				dataHora: new Date(),
				identificador: venda.identificador,
				itens: itensDevolucaoEletronica,
			}

			VendaModule.setEtapaVenda({
				...VendaModule.etapaVenda,
				estado: 'INICIANDO_DEVOLUCAO_NFE',
			})

			if (vendasAEnviar.length === 0) {
				const devolucaoEletronica = await processarVendaDevolucaoEletronica(venda, turnoDeVenda, gerarCupom,
					emitirDanfe, detalhesDosPagamentos, service, itensDevolucaoEletronica, vendaOriginal, vendasAEnviar, codigoDoProcesso)

				VendaModule.iniciarNovaVenda({ identificador, novaVendaReaberta: false })
				UserLoginStore.setAutenticandoParaLiberarVendaSemEstoque(false)

				return devolucaoEletronica
			} else if (!possuiDevolucaoDaVendaOrigem) {
				await processarVendaDevolucaoEletronica(venda, turnoDeVenda, gerarCupom,
					emitirDanfe, detalhesDosPagamentos, service, itensDevolucaoEletronica, vendaOriginal, vendasAEnviar, codigoDoProcesso)

				VendaModule.setEtapaVenda({
					...VendaModule.etapaVenda,
					estado: 'DEVOLUCAO_NFE_CONCLUIDA',
				})
			}
		}
	}

	if (vendasAEnviar.length > 0) {
		let vendaCriadas = vendasAEnviar

		if (codigoDoProcesso < 17) {
			VendaModule.setEtapaVenda({
				vendas: vendaCriadas,
				estado: 'PROCESSANDO_VENDAS',
			})
			vendaCriadas = await service.createVendas(vendasAEnviar)

			VendaModule.setEtapaVenda({
				vendas: vendaCriadas,
				estado: 'VENDAS_CONCLUIDAS',
			})
		}

		if (codigoDoProcesso === 17) {
			vendaCriadas = VendaModule.etapaVenda.vendas
		}

		const venda = vendaCriadas.filter(venda => !venda.isDemonstracao && !venda.isConsignado)[0]
		const sorteioSelecionado = PromocaoModule.dados.sorteioSelecionado
		if (sorteioSelecionado) {
			try {
				const numerosSorteados = await findPromocaoUseCase.gerarNumerosDeSorteioDaVenda(
					venda.id,
					sorteioSelecionado,
				)

				venda.numerosDoSorteio = numerosSorteados
			} catch (error: any) {
				AlertModule.setWarning(
					'Venda finalizada com sucesso, mas ocorreu um erro ao gerar os números do sorteio. Verifique a venda e tente gerar os números depois.',
				)
			}
		}
		const demonstracao = vendaCriadas.filter(venda => venda.isDemonstracao)[0]
		if (demonstracao) {
			if (venda) {
				VendaModule.setEtapaVenda({
					...VendaModule.etapaVenda,
					estado: 'PRECESSANDO_IMPRESSAO_DEMONSTRACAO',
				})
				// Função auxiliar responsável por processar a impressão da DANFE, centralizando a lógica para evitar duplicação de código.
				const processarImpressaoOuDanfe = async () => {
					if (ehUmaVendaFiscal) {
						try {
							await emitirDanfePorVendaId(demonstracao.id)
						} catch (error: any) {
							const erroComPrefixo: ErroComPrefixo = {
								error,
								prefixo: 'Erro ao emitir danfe do retorno, verifique na tela de vendas: ',
							}
							AlertModule.setErrorComPrefixo(erroComPrefixo)
						}
					} else {
						await gerarNotaNaoEletronica(demonstracao.id)
					}
				}

				await processarImpressaoOuDanfe()
				VendaModule.setEtapaVenda({
					...VendaModule.etapaVenda,
					estado: 'FINALIZOU_PRECESSO_IMPRESSAO_DEMONSTRACAO',
				})

				localStorage.setItem('ULTIMA_VENDA', JSON.stringify(venda))
				VendaModule.setEtapaVenda({
					...VendaModule.etapaVenda,
					estado: 'PRECESSANDO_IMPRESSAO_VENDA',
				})

				await imprimirCupomOuDanfe(venda, gerarCupom, emitirDanfe, detalhesDosPagamentos, false)
				VendaModule.setEtapaVenda({
					...VendaModule.etapaVenda,
					estado: 'FINALIZOU_PRECESSO_IMPRESSAO_VENDA',
				})

				VendaModule.iniciarNovaVenda({ identificador, novaVendaReaberta: false })
				UserLoginStore.setAutenticandoParaLiberarVendaSemEstoque(false)

				return venda
			} else {
				VendaModule.setEtapaVenda({
					...VendaModule.etapaVenda,
					estado: 'PRECESSANDO_IMPRESSAO_DEMONSTRACAO',
				})
				await imprimirCupomOuDanfe(demonstracao, gerarCupom, emitirDanfe, detalhesDosPagamentos, false)
				localStorage.setItem('ULTIMA_VENDA', JSON.stringify(demonstracao))
				VendaModule.iniciarNovaVenda({ identificador, novaVendaReaberta: false })
				UserLoginStore.setAutenticandoParaLiberarVendaSemEstoque(false)
				VendaModule.setEtapaVenda({
					...VendaModule.etapaVenda,
					estado: 'FINALIZOU_PRECESSO_IMPRESSAO_DEMONSTRACAO',
				})

				return demonstracao
			}

		} else if (venda && vendaCriadas.length > 1) {
			VendaModule.setEtapaVenda({
				...VendaModule.etapaVenda,
				estado: 'PRECESSANDO_IMPRESSAO_VENDA',
			})
			await imprimirCupomOuDanfe(venda, gerarCupom, emitirDanfe, detalhesDosPagamentos, false)
			VendaModule.setEtapaVenda({
				...VendaModule.etapaVenda,
				estado: 'FINALIZOU_PRECESSO_IMPRESSAO_VENDA',
			})
			localStorage.setItem('ULTIMA_VENDA', JSON.stringify(venda))
			VendaModule.iniciarNovaVenda({ identificador, novaVendaReaberta: false })
			UserLoginStore.setAutenticandoParaLiberarVendaSemEstoque(false)

			return venda
		} else {
			VendaModule.setEtapaVenda({
				...VendaModule.etapaVenda,
				estado: 'PRECESSANDO_IMPRESSAO_VENDA',
			})
			const ImprimirVenda = vendaCriadas[0]
			await imprimirCupomOuDanfe(ImprimirVenda, gerarCupom, emitirDanfe, detalhesDosPagamentos, false)
			VendaModule.setEtapaVenda({
				...VendaModule.etapaVenda,
				estado: 'FINALIZOU_PRECESSO_IMPRESSAO_VENDA',
			})
			localStorage.setItem('ULTIMA_VENDA', JSON.stringify(ImprimirVenda))
			VendaModule.iniciarNovaVenda({ identificador, novaVendaReaberta: false })
			UserLoginStore.setAutenticandoParaLiberarVendaSemEstoque(false)

			return ImprimirVenda
		}
	} else {
		if (itensDevolucao && itensDevolucao.length > 0 && !ehUmaVendaFiscal && venda.vendaIdParaCancelar && codigoDoProcesso < 23) {
			VendaModule.setEtapaVenda({
				...VendaModule.etapaVenda,
				estado: 'PROCESSANDO_CANCELAMENTO_VENDAS',
			})
			await service.cancelar(venda.vendaIdParaCancelar)
			VendaModule.setEtapaVenda({
				...VendaModule.etapaVenda,
				estado: 'PROCESSO_CANCELAMENTO_VENDAS_CONCLUIDO',
			})
		}
		VendaModule.iniciarNovaVenda({ identificador, novaVendaReaberta: false })
		UserLoginStore.setAutenticandoParaLiberarVendaSemEstoque(false)
		return venda
	}
}

async function processarVendaDevolucaoEletronica(venda: Venda, turnoDeVenda: TurnoDeVenda, gerarCupom: boolean,
	emitirDanfe: boolean, detalhesDosPagamentos: DetalhesDoPagamento[], service: VendaServiceAdapter,
	itensDevolucaoEletronica: ItemDaVenda[], vendaOriginal: Venda, vendasAEnviar: Venda[], codigoDoProcesso: number): Promise<Venda> {

	let identificadorDevolucao = ''
	try {
		identificadorDevolucao = await service.gerarIdentificadorDeVenda(turnoDeVenda.pontoDeVenda.id)
	} catch (error: any) {
		throw Error('Falha ao finalizar a venda, tente novamente ou contate o suporte.')
	}

	const vendasOrigemId = Array.from(
		new Set(itensDevolucaoEletronica.filter(item => !!item.vendaOrigemDoItem).map(item => item.vendaOrigemDoItem)),
	)

	const vendaDevolucaoEletronica: Venda = {
		...criarDevolucaoParaDemonstracaoOuConsignado({
			itens: itensDevolucaoEletronica,
			vendaOrigem: venda,
			vendaOriginal: vendaOriginal,
			...turnoDeVenda.pontoDeVenda.parametrosFiscais,
			tipoDeTransacao: 'Devolução',
		}),
		pontoDeVenda: turnoDeVenda.pontoDeVenda,
		dataHora: new Date(),
		identificador: identificadorDevolucao,
		isConsignado: venda.isConsignado,
		isDemonstracao: venda.isDemonstracao,
		cliente: venda.cliente,
		desconto: null,
		vendaOrigem: vendasOrigemId.length === 1 ? vendasOrigemId[0] : null,
	}

	VendaModule.setEtapaVenda({
		...VendaModule.etapaVenda,
		estado: 'INICIANDO_DEVOLUCAO_DEMONSTRACAO_NFE',
	})

	if (vendasOrigemId.length === 1) {
		await validarDevolucao(vendaDevolucaoEletronica)
	}

	if (vendaDevolucaoEletronica.isConsignado && codigoDoProcesso < 10) {
		await service.devolucaoVendaConsignada(vendaDevolucaoEletronica)
	} else if (codigoDoProcesso < 10) {
		await service.devolucaoVendaDemonstracao(vendaDevolucaoEletronica)
	}

	VendaModule.setEtapaVenda({
		...VendaModule.etapaVenda,
		estado: 'DEVOLUCAO_DEMONSTRACAO_NFE_CONCLUIDA',
	})
	if (codigoDoProcesso < 12) {
		try {
			VendaModule.setEtapaVenda({
				...VendaModule.etapaVenda,
				estado: 'GERANDO_NFE',
			})

			await emitirDanfePorVendaId(vendaDevolucaoEletronica.id)

			VendaModule.setEtapaVenda({
				...VendaModule.etapaVenda,
				estado: 'NFE_GERADA',
			})
			if (vendasAEnviar.length === 0) {
				VendaModule.setEtapaVenda({
					...VendaModule.etapaVenda,
					estado: 'IMPRIMINDO_NF_DEVOLUCAO',
				})
				await imprimirCupomOuDanfe(vendaDevolucaoEletronica, gerarCupom, emitirDanfe, detalhesDosPagamentos, false)
				VendaModule.setEtapaVenda({
					...VendaModule.etapaVenda,
					estado: 'IMPRESSAO_NF_DEVOLUCAO_CONCLUIDA',
				})

				return vendaDevolucaoEletronica
			}
		} catch (error: any) {
			const erroComPrefixo = {
				error: error,
				prefixo: 'Erro ao emitir danfe do retorno, verifique na tela de vendas: ',
			} as ErroComPrefixo
			AlertModule.setErrorComPrefixo(erroComPrefixo)
		}
	}

	return vendaDevolucaoEletronica
}

async function processarVendaDevolucaoNaoEletronica(venda: Venda, turnoDeVenda: TurnoDeVenda, service: VendaServiceAdapter,
	itensDevolucaoNaoEletronica: ItemDaVenda[], vendaOriginal: Venda, codigoDoProcesso: number): Promise<Venda> {
	let identificadorDevolucao = ''
	try {
		identificadorDevolucao = await service.gerarIdentificadorDeVenda(turnoDeVenda.pontoDeVenda.id)
	} catch (error: any) {
		throw Error('Falha ao finalizar a venda, tente novamente ou contate o suporte.')
	}

	const vendasOrigemId = Array.from(
		new Set(itensDevolucaoNaoEletronica.filter(item => !!item.vendaOrigemDoItem).map(item => item.vendaOrigemDoItem)),
	)

	const listaDasVendasSemNota = await service.filtrarVendasSemNota({ vendasId: vendasOrigemId } as FiltroDeVendasSemNota)

	const novaListaDeItens = itensDevolucaoNaoEletronica.filter(item =>
		!listaDasVendasSemNota.some(vendaSemNotaId => vendaSemNotaId === item.vendaOrigemDoItem))

	if (listaDasVendasSemNota.length > 0 && codigoDoProcesso < 2) {
		VendaModule.setEtapaVenda({
			...VendaModule.etapaVenda,
			estado: 'CANCELANDO_NNES',
		})
		await service.cancelarVendas(listaDasVendasSemNota)
		VendaModule.setEtapaVenda({
			...VendaModule.etapaVenda,
			estado: 'CANCELAMENTO_NNES_CONCLUIDO',
		})
		if (novaListaDeItens.length === 0) return venda
	}

	const vendaDevolucaoNaoEletronica: Venda = {
		...criarDevolucaoParaDemonstracaoOuConsignado({
			itens: novaListaDeItens,
			vendaOrigem: venda,
			vendaOriginal: vendaOriginal,
			...turnoDeVenda.pontoDeVenda.parametrosFiscais,
			tipoDeTransacao: 'Devolução',
		}),
		pontoDeVenda: turnoDeVenda.pontoDeVenda,
		dataHora: new Date(),
		identificador: identificadorDevolucao,
		isConsignado: venda.isConsignado,
		isDemonstracao: venda.isDemonstracao,
		cliente: venda.cliente,
		desconto: null,
		vendaOrigem: vendasOrigemId.length === 1 ? vendasOrigemId[0] : null,
	}

	if (vendasOrigemId.length === 1) {
		await validarDevolucao(vendaDevolucaoNaoEletronica)
	}

	VendaModule.setEtapaVenda({
		...VendaModule.etapaVenda,
		estado: 'INICIANDO_DEVOLUCAO_DEMONSTRACAO_NNE',
	})

	if (vendaDevolucaoNaoEletronica.isConsignado && codigoDoProcesso < 4) {
		await service.devolucaoVendaConsignada(vendaDevolucaoNaoEletronica)
		VendaModule.setEtapaVenda({
			...VendaModule.etapaVenda,
			estado: 'DEVOLUCAO_DEMONSTRACAO_NNE_CONCLUIDA',
		})
	} else if (codigoDoProcesso < 4) {
		await service.devolucaoVendaDemonstracao(vendaDevolucaoNaoEletronica)
		VendaModule.setEtapaVenda({
			...VendaModule.etapaVenda,
			estado: 'DEVOLUCAO_DEMONSTRACAO_NNE_CONCLUIDA',
		})
	}
	if (codigoDoProcesso < 6) {
		try {
			VendaModule.setEtapaVenda({
				...VendaModule.etapaVenda,
				estado: 'GERANDO_NNE',
			})
			await gerarNotaNaoEletronica(vendaDevolucaoNaoEletronica.id)
			VendaModule.setEtapaVenda({
				...VendaModule.etapaVenda,
				estado: 'NNE_GERADA',
			})
			return vendaDevolucaoNaoEletronica
		} catch (error: any) {
			const erroComPrefixo = {
				error: error,
				prefixo: 'Erro ao emitir danfe do retorno, verifique na tela de vendas: ',
			} as ErroComPrefixo
			AlertModule.setErrorComPrefixo(erroComPrefixo)
		}
	}
	return vendaDevolucaoNaoEletronica
}

async function imprimirCupomOuDanfe(vendaAtualizada: Venda, gerarCupom: boolean, emitirDanfe: boolean, detalhesDosPagamentos: DetalhesDoPagamento[], isVitrineVirtual: boolean) {
	const reservarNumeroDaNota = vendaAtualizada.pagamentos.filter(pagamento => pagamento.tipoDePagamento.reservarNumeroDaNota).length > 0
	
	if (gerarCupom && emitirDanfe) {
		try {
			await imprimirNotaFiscal(vendaAtualizada.id, true, false, reservarNumeroDaNota)
		} catch (error: any) {
			AlertModule.setError(error)
		}
	} else {
		const possuiPagamentoLinkAsaas = vendaAtualizada.pagamentos.filter(pagamento => pagamento.linkIdAsaas != null).length > 0
		if (gerarCupom && (vendaAtualizada.pontoDeVenda?.podeGerarNFCE || vendaAtualizada.pontoDeVenda?.podeGerarNFE)) {
			try {
				await imprimirNotaFiscal(vendaAtualizada.id, false, false, reservarNumeroDaNota)
			} catch (error: any) {
				AlertModule.setError(error)
			}
		}

		PluginModule.emit("finalizarVenda", { venda: vendaAtualizada })

		if (vendaAtualizada.tipoDeTransacao === 'Devolução' || vendaAtualizada.tipoDeTransacao === 'Outros') {
			AlertModule.setSuccess("Devolução realizada com sucesso")
		} else if ((vendaAtualizada.pontoDeVenda?.imprimeReciboNoOrcamento && !gerarCupom) || reservarNumeroDaNota) {
			const vendaComCliente = {
				...vendaAtualizada,
				cliente: vendaAtualizada.cliente,
			}

			if (!vendaAtualizada.pontoDeVenda?.criaPedidoNaVenda && !reservarNumeroDaNota && !possuiPagamentoLinkAsaas && !isVitrineVirtual) {
				try {
					await gerarNotaNaoEletronica(vendaAtualizada.id)
				} catch (error: any) {
					AlertModule.setError(error);
				}
			}

			await imprimirCupomSemValorFiscal(vendaComCliente, detalhesDosPagamentos);
		} else if (!vendaAtualizada.pontoDeVenda?.imprimeReciboNoOrcamento && !gerarCupom) {
			if (!vendaAtualizada.pontoDeVenda?.criaPedidoNaVenda && !reservarNumeroDaNota && !possuiPagamentoLinkAsaas) {
				try {
					await gerarNotaNaoEletronica(vendaAtualizada.id)
				} catch (error: any) {
					AlertModule.setError(error);
				}
			}
		} 
		
		if ((vendaAtualizada.pontoDeVenda?.podeGerarNFCE || vendaAtualizada.pontoDeVenda?.podeGerarNFE) && !gerarCupom && reservarNumeroDaNota && possuiPagamentoLinkAsaas){
			try {
				await imprimirNotaFiscal(vendaAtualizada.id, true, false, reservarNumeroDaNota)
			} catch (error: any) {
				AlertModule.setError(error)
			}
		}
	}
}

async function emitirDanfePorVendaId(vendaId: string) {
	await emitirEComunicarDanfe(vendaId)
}

async function validarEstoque(venda: Venda, turnoDeVenda: TurnoDeVenda, ehUmaDemonstracaoSemNotaEReaberta: boolean,
	ehUmaTroca: boolean, findEstoqueUseCase: FindEstoqueUseCase) {
	const ehUmaDemonstracao = venda.isDemonstracao || venda.isConsignado

	const gerarEstoqueNaVenda = turnoDeVenda.pontoDeVenda.loja.configuracaoDaLoja?.podeGerarEstoqueNaVenda ||
		UserLoginStore.permiteRegraDeNegocio('pode-liberar-produto-sem-estoque') || UserLoginStore.autenticandoParaLiberarVendaSemEstoque

	if ((!gerarEstoqueNaVenda || turnoDeVenda.pontoDeVenda.criaPedidoNaVenda) && venda.tipoDeTransacao === 'Venda'
		&& !ehUmaDemonstracaoSemNotaEReaberta && !ehUmaDemonstracao) {
		const itensUnificados = unificarItensDaVendaPorProduto(venda.itens)
		const podeLiberarEstoque = UserLoginStore.permiteRegraDeNegocio('pode-liberar-produto-sem-estoque') || UserLoginStore.autenticandoParaLiberarVendaSemEstoque
		let projecoesPorItemDaVenda
		if (!podeLiberarEstoque && !VendaModule.vendaAtual?.itens.some(item => item.produto.isValePresente)) {
			projecoesPorItemDaVenda = await Promise.all(
				itensUnificados.map(async (itemDaVenda: any) => {
					let criticas = ''
					await findEstoqueUseCase.getEstoqueDoItemDaVendaNaLoja(
						itemDaVenda.id, itemDaVenda.quantidade.toString(), turnoDeVenda?.pontoDeVenda.loja.id || '', itemDaVenda.autenticadoParaLiberarProdutoSemEstoque,
					).catch((error) => {
						criticas = error instanceof Object ? error.response.data[0] : error
					})
					return criticas
				}),
			)
			projecoesPorItemDaVenda = projecoesPorItemDaVenda.filter(value => value !== '')
		}
		const criticasNormalizadas = projecoesPorItemDaVenda
			? projecoesPorItemDaVenda.join('<br>')
			: undefined
		if (criticasNormalizadas) {
			throw criticasNormalizadas
		}
	}

	//valida estoque somente dos itens positivos(itens da venda)
	if (ehUmaTroca && !gerarEstoqueNaVenda) {
		const itensUnificados = unificarItensDaVendaPorProduto(venda.itens.filter(item => item.quantidade > 0))
		const podeLiberarEstoque = UserLoginStore.permiteRegraDeNegocio('pode-liberar-produto-sem-estoque') || UserLoginStore.autenticandoParaLiberarVendaSemEstoque
		let projecoesPorItemDaVenda
		if (!podeLiberarEstoque && !VendaModule.vendaAtual?.itens.some(item => item.produto.isValePresente)) {
			projecoesPorItemDaVenda = await Promise.all(
				itensUnificados.map(async (itemDaVenda: any) => {
					let criticas = ''
					await findEstoqueUseCase.getEstoqueDoItemDaVendaNaLoja(
						itemDaVenda.id, itemDaVenda.quantidade.toString(), turnoDeVenda?.pontoDeVenda.loja.id || '', itemDaVenda.autenticadoParaLiberarProdutoSemEstoque,
					).catch((error) => {
						criticas = error instanceof Object ? error.response.data[0] : error
					})
					return criticas
				}),
			)
			projecoesPorItemDaVenda = projecoesPorItemDaVenda.filter(value => value !== '')
		}
		const criticasNormalizadas = projecoesPorItemDaVenda
			? projecoesPorItemDaVenda.join('<br>')
			: undefined
		if (criticasNormalizadas) {
			throw criticasNormalizadas
		}
	}
}

async function validarDevolucao(devolucao: Venda) {
	// Busca venda origem por identificador e valida se a venda origem e identica a quantidade e produtos dessa devolucao
	if (!devolucao.vendaOrigem) {
		console.log('[DEBUG] devolucao', devolucao)
		throw new Error('Venda origem não informada para validação da devolução')
	}

	try {
		const vendaOrigem = await findVendaUseCase.get(devolucao.vendaOrigem)
		
		if (!vendaOrigem) {
			throw new Error('Venda origem não encontrada')
		}

		if (vendaOrigem.situacao === 'Cancelada') {
			throw new Error('Não é possível devolver uma venda cancelada')
		}

		if (vendaOrigem.tipoDeTransacao === 'Devolução') {
			throw new Error('Não é possível devolver uma devolução')
		}

		await validarQuantidadeEProdutosDevolucao(devolucao, vendaOrigem)

	} catch (error: any) {
		throw new Error(`Erro na validação da devolução: ${error.message}`)
	}
}

async function validarQuantidadeEProdutosDevolucao(devolucao: Venda, vendaOrigem: Venda) {
	// Valida se todos os produtos da devolucao existem na venda origem perconrrendo os itens da devolucao
	for (const itemDevolucao of devolucao.itens) {
		const itemOrigem = vendaOrigem.itens.find(item => 
			(item.produto.id === itemDevolucao.produto.id || 
			item.id === itemDevolucao.idItemOrigem) && Math.abs(item.quantidade) === Math.abs(itemDevolucao.quantidade),
		)

		if (!itemOrigem) {
			throw new Error(`Produto ${itemDevolucao.produto.nome} não encontrado na venda origem (ID produto: ${itemDevolucao.produto.id}, Qtd item: ${itemDevolucao.quantidade})`)
		}

		// Valida se a quantidade da devolucao nao ultrapassa a quantidade original
		const quantidadeOriginal = itemOrigem.quantidade
		const quantidadeDevolucao = Math.abs(itemDevolucao.quantidade) // Quantidade sempre negativa na devolucao

		if (quantidadeDevolucao > quantidadeOriginal) {
			throw new Error(
				`Quantidade de devolução (${quantidadeDevolucao}) do produto ${itemDevolucao.produto.nome} ` +
				`excede a quantidade original (${quantidadeOriginal}), contate o suporte.`,
			)
		}
	}

	// Valida se o total da devolucao não excede o total da venda origem, descomendar se for necessario validar isso
	/*
	const totalDevolucao = Math.abs(devolucao.itens.reduce((total, item) => total + (item.preco * item.quantidade), 0))
	const totalOrigem = vendaOrigem.itens.reduce((total, item) => total + (item.preco * item.quantidade), 0)

	if (totalDevolucao > totalOrigem) {
		throw new Error(
			`Valor total da devolução (${totalDevolucao.toFixed(2)}) ` +
			`excede o valor total da venda origem (${totalOrigem.toFixed(2)})`,
		)
	}
	*/
}

async function finalizarSomenteItensDeVitrine({
	venda,
	itensDeVitrine,
	turnoDeVenda,
	service,
	detalhesDosPagamentos,
	modalidade,
	canalDeVenda,
}: {
	venda: Venda
	itensDeVitrine: ItemDaVenda[]
	turnoDeVenda: TurnoDeVenda
	service: VendaServiceAdapter
	detalhesDosPagamentos: DetalhesDoPagamento[]
	modalidade: ModalidadeDeVenda
	canalDeVenda: CanaldeVendas
}): Promise<Venda> {
	const identificador = await service.gerarIdentificadorDeVenda(turnoDeVenda.pontoDeVenda.id)

	const vendaVitrine: Venda = {
		...venda,
		id: '',
		identificador,
		itens: itensDeVitrine,
		pontoDeVenda: {
			...turnoDeVenda.pontoDeVenda,
			criaPedidoNaVenda: true,
		},
		dataHora: venda.dataHora || new Date(),
		modalidadeDeVenda: modalidade,
		canalDeVendas: canalDeVenda,
	}

	VendaModule.setEtapaVenda({
		...VendaModule.etapaVenda,
		estado: 'PROCESSANDO_VENDA_FINAL',
	})
	const vendaAtualizada = await service.create(vendaVitrine, false, true)
	VendaModule.setEtapaVenda({
		vendas: [vendaAtualizada],
		estado: 'PROCESSO_VENDA_FINAL_CONLCUIDO',
	})

	// Nova venda
	if (!venda.identificador) VendaModule.adicionarVendaDoTurno(vendaAtualizada)
	await imprimirCupomOuDanfe(vendaAtualizada, false, false, detalhesDosPagamentos, true)

	localStorage.setItem('ULTIMA_VENDA', JSON.stringify(vendaAtualizada))

	VendaModule.iniciarNovaVenda({ identificador, novaVendaReaberta: false })
	UserLoginStore.setAutenticandoParaLiberarVendaSemEstoque(false)

	return vendaAtualizada
}

async function finalizarItensDeVitrineSeparadamente({
	venda,
	itensDeVitrine,
	turnoDeVenda,
	service,
	modalidade,
	canalDeVenda,
}: {
	venda: Venda
	itensDeVitrine: ItemDaVenda[]
	turnoDeVenda: TurnoDeVenda
	service: VendaServiceAdapter
	modalidade: ModalidadeDeVenda
	canalDeVenda: CanaldeVendas
}): Promise<Venda> {
	const identificador = await service.gerarIdentificadorDeVenda(turnoDeVenda.pontoDeVenda.id)

	const vendaVitrine: Venda = {
		...venda,
		id: '',
		identificador,
		itens: itensDeVitrine,
		pontoDeVenda: {
			...turnoDeVenda.pontoDeVenda,
			criaPedidoNaVenda: true,
		},
		pedido: null,
		dataHora: venda.dataHora || new Date(),
		modalidadeDeVenda: modalidade,
		canalDeVendas: canalDeVenda,
	}

	return await service.create(vendaVitrine, false, true)
}

function ratearPagamentos(pagamentos: Pagamento[], proporcao: number): Pagamento[] {
	return pagamentos.map((pagamento, index, array) => { if (index === array.length - 1) {
		const somaAnterior = array .slice(0, index) .reduce((acc, p) => acc + Number((p.valor || 0) * proporcao), 0) 
		const valorOriginal = array.reduce((acc, p) => acc + Number(p.valor || 0), 0) 
		const valorRateadoTotal = Number((valorOriginal * proporcao).toFixed(2)) 
		const valorRestante = Number((valorRateadoTotal - somaAnterior).toFixed(2)) 
		return { ...pagamento, valor: valorRestante } } 
	return { ...pagamento, valor: Number((Number(pagamento.valor || 0) * proporcao).toFixed(2)) } }).filter(p => p.valor > 0) 
}

function ratearDesconto(
	desconto: Desconto | null | undefined,
	totalVitrine: number,
	totalNormal: number,
): {
	descontoVitrine: Desconto | null
	descontoNormal: Desconto | null 
	} {

	if (!desconto || !desconto.valor) {
		return {
			descontoVitrine: null,
			descontoNormal: null,
		}
	}

	const totalGeral = Number((totalVitrine + totalNormal).toFixed(2))

	if (totalGeral <= 0) {
		return {
			descontoVitrine: null,
			descontoNormal: null,
		}
	}

	if (desconto.isPercentual) {
		return {
			descontoVitrine: {
				isPercentual: true,
				valor: Number(desconto.valor || 0),
			},
			descontoNormal: {
				isPercentual: true,
				valor: Number(desconto.valor || 0),
			},
		}
	}

	const valorDescontoTotal = Number(desconto.valor || 0)

	const valorDescontoVitrine = Number(((valorDescontoTotal * totalVitrine) / totalGeral).toFixed(2))
	const valorDescontoNormal = Number((valorDescontoTotal - valorDescontoVitrine).toFixed(2))

	const percentualVitrine = totalVitrine > 0
		? Number(((valorDescontoVitrine / totalVitrine) * 100).toFixed(4))
		: 0

	const percentualNormal = totalNormal > 0
		? Number(((valorDescontoNormal / totalNormal) * 100).toFixed(4))
		: 0

	return {
		descontoVitrine: valorDescontoVitrine > 0 ? {
			isPercentual: true,
			valor: percentualVitrine,
		} : null,
		descontoNormal: valorDescontoNormal > 0 ? {
			isPercentual: true,
			valor: percentualNormal,
		} : null,
	}
}

function calcularValorLiquidoDoItem(item: ItemDaVenda): number {
	const valorBruto = Number(item.preco) * Number(item.quantidade)
	const desconto = item.desconto

	if (!desconto || !desconto.valor) return valorBruto

	if (desconto.isPercentual) {
		return Number((valorBruto * (1 - desconto.valor / 100)).toFixed(2))
	}

	return Number((valorBruto - Number(desconto.valor)).toFixed(2))
}